import { ColumnFilter, ColumnFiltersState, Table } from '@tanstack/react-table';
import { Dispatch, SetStateAction, useMemo } from 'react';

import { DateRangeValue } from '../CalendarPicker/CalendarPicker';

export interface FilterTagGroup {
  filterId: string;
  title: string;
  values: string[];
  labels: string[];
}

export type FilterMeta = Record<string, { title: string; getLabel: (v: string) => string }>;

function isDateRangeValue(v: unknown): v is DateRangeValue {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && ('after' in v || 'before' in v);
}

function formatDateRangeLabel(v: DateRangeValue): string {
  const fmt = (d: Date) =>
    new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
  if (v.after && v.before) return `${fmt(v.after)} – ${fmt(v.before)}`;
  if (v.after) return `from ${fmt(v.after)}`;
  return `to ${fmt(v.before)}`;
}

interface UseFilterTagsOptions<TData> {
  columnFilters: ColumnFilter[];
  setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>;
  setLoader: (val: boolean) => void;
  filterMeta: FilterMeta;
  table?: Table<TData>;
}

export function useFilterTags<TData>({
  columnFilters,
  setColumnFilters,
  setLoader,
  filterMeta,
  table,
}: UseFilterTagsOptions<TData>) {
  const activeFilterGroups = useMemo((): FilterTagGroup[] => {
    if (!columnFilters) return [];
    return columnFilters
      .filter(f => {
        if (Array.isArray(f.value)) return (f.value as string[]).length > 0;
        if (isDateRangeValue(f.value)) return Boolean(f.value.after || f.value.before);
        return false;
      })
      .map(f => {
        const meta = filterMeta[f.id];
        const colTitle = meta?.title ?? table?.getColumn(f.id)?.columnDef.meta?.title ?? f.id;

        if (isDateRangeValue(f.value)) {
          const label = formatDateRangeLabel(f.value);
          return { filterId: f.id, title: colTitle, values: ['__daterange__'], labels: [label] };
        }

        const values = f.value as string[];
        if (meta) {
          return { filterId: f.id, title: meta.title, values, labels: values.map(v => meta.getLabel(v)) };
        }
        return { filterId: f.id, title: colTitle, values, labels: values };
      });
  }, [columnFilters, filterMeta, table]);

  const removeFilterGroup = (filterId: string) => {
    setLoader(true);
    setColumnFilters(prev => (prev ?? []).filter(f => f.id !== filterId));
  };

  const removeFilterValue = (filterId: string, value: string) => {
    setLoader(true);
    setColumnFilters(prev => {
      if (!prev) return prev;
      // Date range filters use a sentinel value — removing it clears the whole filter
      if (value === '__daterange__') {
        return prev.filter(f => f.id !== filterId);
      }
      return prev
        .map(f => {
          if (f.id !== filterId) return f;
          const newValues = (f.value as string[]).filter(v => v !== value);
          return newValues.length ? { ...f, value: newValues } : null;
        })
        .filter(Boolean) as ColumnFilter[];
    });
  };

  return { activeFilterGroups, removeFilterGroup, removeFilterValue };
}
